import type {ProcessRisksAndControls} from "../../types";

export const PROCESS_RISKS_AND_CONTROLS: ProcessRisksAndControls[] = [
  // Vendor invoice processing & three-way match
  {
    processId: "proc-vendor-invoice",
    risks: [
      {
        id: "risk-vi-01",
        riskNumber: "R-01",
        title: "Unauthorized purchases",
        description:
          "Purchase orders may be created or approved by individuals without proper authorization, leading to unapproved spending and potential fraud.",
        owner: "Jenny Zhan",
        inherentScore: {
          components: {complexity: 4, subjectivity: 3, change: 5, uncertainty: 4, fraudSusceptibility: 5},
          rationale:
            "Very high inherent fraud susceptibility in procurement with manual approval pathways. Recent ERP migration introduces maximum change risk, and complex multi-tier authorization requirements add uncertainty.",
          assessedBy: "Platformed AI",
          assessedAt: new Date("2025-12-01"),
        },
        residualLevel: "significant",
        residualScore: {
          components: {complexity: 3, subjectivity: 2, change: 4, uncertainty: 3, fraudSusceptibility: 4},
          rationale:
            "Manager approval (AP-01) and tiered authorization limits (AP-02) reduce fraud susceptibility and subjectivity, but cannot fully address the change risk from the ongoing ERP migration. Residual risk remains high as controls are still being embedded in the new system.",
          assessedBy: "Platformed AI",
          assessedAt: new Date("2025-12-01"),
        },
        status: "complete",
        cotabdAssertions: [
          {cotabdId: "cotabd-exp-transactions", assertionId: "existence"},
          {cotabdId: "cotabd-exp-balance", assertionId: "existence"},
          {cotabdId: "cotabd-exp-balance", assertionId: "rights"},
        ],
        controls: [
          {
            id: "ctrl-vi-01a",
            controlNumber: "AP-01",
            title: "Purchase Requisition Manager Approval",
            description: "All purchase requisitions require direct manager approval before forwarding to procurement.",
            designScore: 3,
            implementationScore: 3,
            lastAssessed: new Date("2025-11-15"),
            status: "complete",
          },
          {
            id: "ctrl-vi-01b",
            controlNumber: "AP-02",
            title: "Purchase Order Approval Limits",
            description:
              "Purchase orders above $50,000 require additional purchasing manager approval with documented authorization matrix.",
            designScore: 3,
            implementationScore: 3,
            lastAssessed: new Date("2025-11-10"),
            status: "complete",
          },
        ],
      },
      {
        id: "risk-vi-02",
        riskNumber: "R-02",
        title: "Duplicate payments",
        description:
          "Vendor invoices may be processed and paid more than once due to resubmission or system errors, resulting in overpayments.",
        owner: "James Park",
        inherentScore: {
          components: {complexity: 4, subjectivity: 2, change: 3, uncertainty: 3, fraudSusceptibility: 3},
          rationale:
            "High inherent complexity from multi-step matching across procurement, receiving, and AP systems. Vendor resubmission patterns create moderate fraud susceptibility and uncertainty.",
          assessedBy: "Platformed AI",
          assessedAt: new Date("2025-12-01"),
        },
        residualLevel: "medium",
        residualScore: {
          components: {complexity: 3, subjectivity: 2, change: 2, uncertainty: 2, fraudSusceptibility: 2},
          rationale:
            "The automated three-way match (AP-04) effectively reduces duplicate payment risk across most dimensions, though partial match implementation leaves some complexity. Change and uncertainty lowered as the matching rules are well-established.",
          assessedBy: "Platformed AI",
          assessedAt: new Date("2025-12-01"),
        },
        status: "in-review",
        cotabdAssertions: [
          {cotabdId: "cotabd-exp-transactions", assertionId: "completeness"},
          {cotabdId: "cotabd-exp-transactions", assertionId: "accuracy"},
          {cotabdId: "cotabd-exp-balance", assertionId: "completeness"},
          {cotabdId: "cotabd-exp-disclosure", assertionId: "completeness"},
          {cotabdId: "cotabd-exp-disclosure", assertionId: "accuracy"},
        ],
        controls: [
          {
            id: "ctrl-vi-02a",
            controlNumber: "AP-04",
            title: "Three-Way Match Requirement",
            description:
              "System enforces automated matching of purchase order, goods receipt, and vendor invoice before payment release.",
            designScore: 3,
            implementationScore: 2,
            lastAssessed: new Date("2025-11-12"),
            status: "in-review",
          },
        ],
      },
      {
        id: "risk-vi-03",
        riskNumber: "R-03",
        title: "Incorrect GL coding",
        description:
          "Vendor invoices may be posted to incorrect general ledger accounts, leading to misstatement of expenses and account balances.",
        owner: "Jenny Zhan",
        inherentScore: {
          components: {complexity: 3, subjectivity: 3, change: 2, uncertainty: 2, fraudSusceptibility: 1},
          rationale:
            "Moderate inherent complexity and subjectivity in choosing correct GL accounts across diverse purchase categories. Low fraud susceptibility as miscoding is typically an error rather than intentional.",
          assessedBy: "Platformed AI",
          assessedAt: new Date("2025-12-01"),
        },
        residualLevel: "low",
        residualScore: {
          components: {complexity: 2, subjectivity: 2, change: 1, uncertainty: 1, fraudSusceptibility: 1},
          rationale:
            "Default GL account mappings (AP-06) and monthly distribution reviews significantly reduce coding subjectivity and complexity. The stable chart of accounts means the control environment is well-suited to this risk.",
          assessedBy: "Platformed AI",
          assessedAt: new Date("2025-12-01"),
        },
        status: "in-progress",
        cotabdAssertions: [
          {cotabdId: "cotabd-exp-transactions", assertionId: "accuracy"},
          {cotabdId: "cotabd-exp-disclosure", assertionId: "accuracy"},
          {cotabdId: "cotabd-exp-disclosure", assertionId: "presentation"},
        ],
        controls: [
          {
            id: "ctrl-vi-03a",
            controlNumber: "AP-06",
            title: "GL Account Validation",
            description:
              "Default GL account mapping based on purchase category with monthly review of account distribution by accounting manager.",
            designScore: null,
            implementationScore: null,
            lastAssessed: null,
            status: "not-ready",
          },
        ],
      },
    ],
  },
  // Employee expense reimbursement
  {
    processId: "proc-expense-reimbursement",
    risks: [
      {
        id: "risk-er-01",
        riskNumber: "R-01",
        title: "Non-compliant expenses",
        description:
          "Employee expense claims may include items not permitted under the travel and expense policy, inflating reported costs.",
        owner: "James Park",
        inherentScore: {
          components: {complexity: 3, subjectivity: 4, change: 2, uncertainty: 3, fraudSusceptibility: 3},
          rationale:
            "High inherent subjectivity — expense policy boundaries are often unclear for entertainment, travel upgrades, and mixed-use purchases. Decentralised claim submission adds uncertainty.",
          assessedBy: "Platformed AI",
          assessedAt: new Date("2025-11-28"),
        },
        residualLevel: "medium",
        residualScore: {
          components: {complexity: 2, subjectivity: 3, change: 2, uncertainty: 2, fraudSusceptibility: 2},
          rationale:
            "Automated Concur policy checks (EXP-01) effectively catch clear violations and reduce complexity, but cannot resolve judgment-based edge cases. Subjectivity remains the primary residual driver as manager interpretation still varies.",
          assessedBy: "Platformed AI",
          assessedAt: new Date("2025-11-28"),
        },
        status: "complete",
        cotabdAssertions: [{cotabdId: "cotabd-exp-transactions", assertionId: "existence"}],
        controls: [
          {
            id: "ctrl-er-01a",
            controlNumber: "EXP-01",
            title: "Policy Compliance Review",
            description:
              "Concur enforces automated policy checks on expense type, amount, and receipt requirements before manager review.",
            designScore: 3,
            implementationScore: 3,
            lastAssessed: new Date("2025-10-28"),
            status: "complete",
          },
        ],
      },
      {
        id: "risk-er-02",
        riskNumber: "R-02",
        title: "Fraudulent expense claims",
        description:
          "Employees may submit fabricated or inflated receipts, claim personal expenses as business costs, or submit duplicate claims.",
        owner: "Jenny Zhan",
        inherentScore: {
          components: {complexity: 3, subjectivity: 3, change: 2, uncertainty: 4, fraudSusceptibility: 5},
          rationale:
            "Very high inherent fraud susceptibility — fabricated receipts and personal expense claims are a well-known risk pattern. High uncertainty as fraudulent claims are difficult to detect without targeted investigation.",
          assessedBy: "Jenny Zhan",
          assessedAt: new Date("2025-11-25"),
        },
        residualLevel: "medium",
        residualScore: {
          components: {complexity: 2, subjectivity: 2, change: 1, uncertainty: 3, fraudSusceptibility: 3},
          rationale:
            "Manager approval (EXP-02) provides a detective layer but relies on managers recognising anomalies. Random audit sampling (EXP-03) adds deterrence but 10% coverage leaves residual uncertainty. Fraud susceptibility reduced but not eliminated.",
          assessedBy: "Jenny Zhan",
          assessedAt: new Date("2025-11-25"),
        },
        status: "in-review",
        cotabdAssertions: [
          {cotabdId: "cotabd-exp-transactions", assertionId: "existence"},
          {cotabdId: "cotabd-exp-transactions", assertionId: "accuracy"},
          {cotabdId: "cotabd-exp-disclosure", assertionId: "accuracy"},
        ],
        controls: [
          {
            id: "ctrl-er-02a",
            controlNumber: "EXP-02",
            title: "Manager Approval",
            description:
              "Direct manager reviews and approves all expense reports with supporting documentation before payment processing.",
            designScore: 3,
            implementationScore: 3,
            lastAssessed: new Date("2025-10-25"),
            status: "complete",
          },
          {
            id: "ctrl-er-02b",
            controlNumber: "EXP-03",
            title: "Random Audit Sampling",
            description:
              "Internal audit performs quarterly random sampling of 10% of expense reports for detailed review.",
            designScore: null,
            implementationScore: null,
            lastAssessed: null,
            status: "not-ready",
          },
        ],
      },
    ],
  },
  // Corporate credit card reconciliation
  {
    processId: "proc-corp-card",
    risks: [
      {
        id: "risk-cc-01",
        riskNumber: "R-01",
        title: "Unauthorized charges",
        description:
          "Corporate card may be used for unauthorized personal purchases or purchases outside of approved spending categories.",
        owner: "Lisa Wong",
        inherentScore: {
          components: {complexity: 2, subjectivity: 3, change: 2, uncertainty: 3, fraudSusceptibility: 4},
          rationale:
            "High inherent fraud susceptibility — corporate cards provide direct spending capability with delayed oversight. Subjectivity around what constitutes a legitimate business expense adds risk.",
          assessedBy: "Platformed AI",
          assessedAt: new Date("2025-11-20"),
        },
        residualLevel: "medium",
        residualScore: {
          components: {complexity: 2, subjectivity: 2, change: 1, uncertainty: 2, fraudSusceptibility: 3},
          rationale:
            "Merchant category restrictions (CC-01) provide a strong preventive control at the network level, reducing subjectivity and uncertainty. However, fraud susceptibility remains significant as MCC blocking cannot catch all personal use within allowed categories.",
          assessedBy: "Platformed AI",
          assessedAt: new Date("2025-11-20"),
        },
        status: "complete",
        cotabdAssertions: [{cotabdId: "cotabd-exp-transactions", assertionId: "existence"}],
        controls: [
          {
            id: "ctrl-cc-01a",
            controlNumber: "CC-01",
            title: "Merchant Category Restrictions",
            description:
              "Card programme restricts transactions by merchant category code, blocking non-business categories at the network level.",
            designScore: 3,
            implementationScore: 3,
            lastAssessed: new Date("2025-10-20"),
            status: "complete",
          },
        ],
      },
      {
        id: "risk-cc-02",
        riskNumber: "R-02",
        title: "Late reconciliation",
        description:
          "Delayed reconciliation of corporate card statements may result in inaccurate expense recognition and missed cut-off dates.",
        owner: "Lisa Wong",
        inherentScore: {
          components: {complexity: 3, subjectivity: 2, change: 2, uncertainty: 4, fraudSusceptibility: 1},
          rationale:
            "High inherent uncertainty as reconciliation timeliness depends entirely on individual cardholder behaviour. Complexity from matching card transactions to receipts across a distributed workforce.",
          assessedBy: "Platformed AI",
          assessedAt: new Date("2025-11-20"),
        },
        residualLevel: "medium",
        residualScore: {
          components: {complexity: 2, subjectivity: 1, change: 2, uncertainty: 3, fraudSusceptibility: 1},
          rationale:
            "Monthly reconciliation requirements and automated reminders (CC-02) reduce complexity and subjectivity, but the control relies on cardholder compliance, so uncertainty remains elevated. The partially effective control design limits further risk reduction.",
          assessedBy: "Platformed AI",
          assessedAt: new Date("2025-11-20"),
        },
        status: "in-review",
        cotabdAssertions: [
          {cotabdId: "cotabd-exp-transactions", assertionId: "cutoff"},
          {cotabdId: "cotabd-exp-transactions", assertionId: "completeness"},
        ],
        controls: [
          {
            id: "ctrl-cc-02a",
            controlNumber: "CC-02",
            title: "Monthly Statement Reconciliation",
            description:
              "Cardholders must reconcile and submit expense reports within 10 business days of statement close with automated reminders.",
            designScore: 2,
            implementationScore: 2,
            lastAssessed: new Date("2025-11-01"),
            status: "in-progress",
          },
        ],
      },
    ],
  },
  // Customer order fulfillment & billing
  {
    processId: "proc-order-fulfillment",
    risks: [
      {
        id: "risk-of-01",
        riskNumber: "R-01",
        title: "Revenue recognised before delivery",
        description:
          "Revenue may be recorded before control of goods transfers to the customer, violating ASC 606 recognition criteria.",
        owner: "Michael Torres",
        inherentScore: {
          components: {complexity: 3, subjectivity: 2, change: 2, uncertainty: 3, fraudSusceptibility: 3},
          rationale:
            "Moderate inherent complexity in determining the point of transfer for different delivery arrangements. Fraud susceptibility from channel-stuffing pressure and uncertainty around in-transit shipments.",
          assessedBy: "Platformed AI",
          assessedAt: new Date("2025-11-15"),
        },
        residualLevel: "low",
        residualScore: {
          components: {complexity: 2, subjectivity: 1, change: 1, uncertainty: 1, fraudSusceptibility: 2},
          rationale:
            "The automated billing trigger (AR-04) tied to carrier shipment confirmation eliminates manual judgment and virtually removes subjectivity and uncertainty from the recognition point. Only minor residual fraud susceptibility from potential carrier data manipulation.",
          assessedBy: "Platformed AI",
          assessedAt: new Date("2025-11-15"),
        },
        status: "complete",
        cotabdAssertions: [
          {cotabdId: "cotabd-rev-transactions", assertionId: "existence"},
          {cotabdId: "cotabd-rev-transactions", assertionId: "cutoff"},
        ],
        controls: [
          {
            id: "ctrl-of-01a",
            controlNumber: "AR-04",
            title: "Automated Billing Trigger",
            description:
              "Invoice generation is system-triggered only upon carrier shipment confirmation, preventing premature billing.",
            designScore: 3,
            implementationScore: 3,
            lastAssessed: new Date("2025-11-08"),
            status: "complete",
          },
        ],
      },
      {
        id: "risk-of-02",
        riskNumber: "R-02",
        title: "Unrecorded shipments",
        description:
          "Goods may be shipped without a corresponding sales order or invoice, leading to unrecognised revenue and inventory discrepancies.",
        owner: "Michael Torres",
        inherentScore: {
          components: {complexity: 3, subjectivity: 2, change: 2, uncertainty: 3, fraudSusceptibility: 2},
          rationale:
            "Moderate inherent complexity from the handoff between warehouse operations and billing systems. Uncertainty around completeness of the shipment-to-order linkage across multiple fulfilment centres.",
          assessedBy: "Platformed AI",
          assessedAt: new Date("2025-11-15"),
        },
        residualLevel: "low",
        residualScore: {
          components: {complexity: 2, subjectivity: 1, change: 1, uncertainty: 2, fraudSusceptibility: 1},
          rationale:
            "Independent shipment verification and daily shipping-to-invoice reconciliation (AR-03) provide strong detective coverage. System-enforced linkage between sales orders and picking eliminates most completeness gaps, with only minor residual uncertainty from same-day reconciliation timing.",
          assessedBy: "Platformed AI",
          assessedAt: new Date("2025-11-15"),
        },
        status: "complete",
        cotabdAssertions: [
          {cotabdId: "cotabd-rev-transactions", assertionId: "completeness"},
          {cotabdId: "cotabd-rev-transactions", assertionId: "existence"},
        ],
        controls: [
          {
            id: "ctrl-of-02a",
            controlNumber: "AR-03",
            title: "Independent Verification of Shipment",
            description:
              "Warehouse picking is verified against approved sales order; shipping log reconciled daily to invoices generated.",
            designScore: 3,
            implementationScore: 3,
            lastAssessed: new Date("2025-11-05"),
            status: "complete",
          },
        ],
      },
    ],
  },
  // Contract revenue recognition
  {
    processId: "proc-contract-rev-rec",
    risks: [
      {
        id: "risk-rr-01",
        riskNumber: "R-01",
        title: "Incorrect performance obligation identification",
        description:
          "Multi-element arrangements may be incorrectly decomposed into performance obligations, leading to misstated revenue timing.",
        owner: "Jenny Zhan",
        inherentScore: {
          components: {complexity: 5, subjectivity: 5, change: 4, uncertainty: 4, fraudSusceptibility: 3},
          rationale:
            "Very high inherent complexity and subjectivity — ASC 606 performance obligation identification for bundled arrangements requires deep judgment. New product bundles and evolving deal structures contribute high change and uncertainty.",
          assessedBy: "Platformed AI",
          assessedAt: new Date("2025-12-02"),
        },
        residualLevel: "significant",
        residualScore: {
          components: {complexity: 4, subjectivity: 4, change: 3, uncertainty: 3, fraudSusceptibility: 2},
          rationale:
            "The Contract Review Committee (REV-01) provides expert oversight for large contracts, reducing fraud susceptibility and providing some standardisation. However, the committee's partially effective design and the inherent judgment required mean complexity and subjectivity remain high.",
          assessedBy: "Platformed AI",
          assessedAt: new Date("2025-12-02"),
        },
        status: "in-review",
        cotabdAssertions: [
          {cotabdId: "cotabd-rev-transactions", assertionId: "accuracy"},
          {cotabdId: "cotabd-rev-transactions", assertionId: "cutoff"},
          {cotabdId: "cotabd-rev-disclosure", assertionId: "accuracy"},
          {cotabdId: "cotabd-rev-disclosure", assertionId: "presentation"},
        ],
        controls: [
          {
            id: "ctrl-rr-01a",
            controlNumber: "REV-01",
            title: "Contract Review Committee",
            description:
              "All contracts over $500K or with non-standard terms are reviewed by the revenue recognition committee before booking.",
            designScore: 2,
            implementationScore: 3,
            lastAssessed: new Date("2025-10-30"),
            status: "not-ready",
          },
        ],
      },
      {
        id: "risk-rr-02",
        riskNumber: "R-02",
        title: "Misstated transaction price",
        description:
          "Variable consideration, discounts, or stand-alone selling price allocation errors may result in incorrect revenue amounts.",
        owner: "James Park",
        inherentScore: {
          components: {complexity: 5, subjectivity: 4, change: 4, uncertainty: 5, fraudSusceptibility: 3},
          rationale:
            "Very high inherent complexity and uncertainty from variable consideration estimates, SSP allocation across bundled products, and frequent changes to discount programmes. Subjectivity in constraint assessments adds further risk.",
          assessedBy: "James Park",
          assessedAt: new Date("2025-12-03"),
        },
        residualLevel: "significant",
        residualScore: {
          components: {complexity: 4, subjectivity: 3, change: 3, uncertainty: 4, fraudSusceptibility: 2},
          rationale:
            "Quarterly SSP analysis (REV-02) and monthly revenue waterfall review (REV-03) provide layered oversight, reducing fraud susceptibility and some subjectivity. However, SSP analysis has not yet been assessed, and the fundamental estimation uncertainty around variable consideration limits the achievable risk reduction.",
          assessedBy: "James Park",
          assessedAt: new Date("2025-12-03"),
        },
        status: "in-progress",
        cotabdAssertions: [
          {cotabdId: "cotabd-rev-transactions", assertionId: "accuracy"},
          {cotabdId: "cotabd-rev-disclosure", assertionId: "accuracy"},
        ],
        controls: [
          {
            id: "ctrl-rr-02a",
            controlNumber: "REV-02",
            title: "SSP Analysis",
            description:
              "Quarterly update of stand-alone selling price analysis with documented methodology and approval.",
            designScore: null,
            implementationScore: null,
            lastAssessed: null,
            status: "not-ready",
          },
          {
            id: "ctrl-rr-02b",
            controlNumber: "REV-03",
            title: "Revenue Waterfall Review",
            description:
              "Controller reviews monthly revenue waterfall report comparing recognised revenue to contract milestones.",
            designScore: 3,
            implementationScore: 3,
            lastAssessed: new Date("2025-11-02"),
            status: "complete",
          },
        ],
      },
    ],
  },
  // Customer credit & collections
  {
    processId: "proc-credit-collections",
    risks: [
      {
        id: "risk-cl-01",
        riskNumber: "R-01",
        title: "Inadequate bad debt allowance",
        description:
          "Allowance for doubtful accounts may not reflect current expected credit losses, leading to overstatement of receivables.",
        owner: "Lisa Wong",
        inherentScore: {
          components: {complexity: 3, subjectivity: 4, change: 3, uncertainty: 4, fraudSusceptibility: 2},
          rationale:
            "High inherent subjectivity and uncertainty in estimating expected credit losses, particularly given macroeconomic volatility. New customer segments with limited loss history increase change risk.",
          assessedBy: "Platformed AI",
          assessedAt: new Date("2025-11-22"),
        },
        residualLevel: "medium",
        residualScore: {
          components: {complexity: 2, subjectivity: 3, change: 2, uncertainty: 3, fraudSusceptibility: 1},
          rationale:
            "Monthly aging analysis and write-off recommendations (AR-07) provide structured data inputs that reduce estimation complexity and change risk. However, judgment in determining appropriate allowance rates for newer segments means subjectivity and uncertainty persist at significant levels.",
          assessedBy: "Platformed AI",
          assessedAt: new Date("2025-11-22"),
        },
        status: "in-progress",
        cotabdAssertions: [{cotabdId: "cotabd-rev-balance", assertionId: "valuation"}],
        controls: [
          {
            id: "ctrl-cl-01a",
            controlNumber: "AR-07",
            title: "Aging Analysis Review",
            description:
              "Monthly aging analysis reviewed by AR manager with write-off recommendations for accounts past 90 days.",
            designScore: 3,
            implementationScore: 3,
            lastAssessed: new Date("2025-11-03"),
            status: "complete",
          },
        ],
      },
      {
        id: "risk-cl-02",
        riskNumber: "R-02",
        title: "Unauthorized write-offs",
        description:
          "Accounts receivable may be written off without proper authorization, masking misappropriation or circumventing collection procedures.",
        owner: "Michael Torres",
        inherentScore: {
          components: {complexity: 2, subjectivity: 2, change: 1, uncertainty: 2, fraudSusceptibility: 4},
          rationale:
            "High inherent fraud susceptibility — unauthorized write-offs are a classic method to conceal misappropriation. Relatively low complexity as the process is straightforward.",
          assessedBy: "Platformed AI",
          assessedAt: new Date("2025-11-22"),
        },
        residualLevel: "low",
        residualScore: {
          components: {complexity: 1, subjectivity: 1, change: 1, uncertainty: 1, fraudSusceptibility: 2},
          rationale:
            "Controller sign-off for write-offs above $10K and quarterly CFO reporting (AR-08) provide strong preventive and detective controls that substantially reduce fraud susceptibility. Clear authorization thresholds eliminate subjectivity.",
          assessedBy: "Platformed AI",
          assessedAt: new Date("2025-11-22"),
        },
        status: "complete",
        cotabdAssertions: [
          {cotabdId: "cotabd-rev-balance", assertionId: "existence"},
          {cotabdId: "cotabd-rev-balance", assertionId: "rights"},
        ],
        controls: [
          {
            id: "ctrl-cl-02a",
            controlNumber: "AR-08",
            title: "Write-Off Authorization",
            description: "All write-offs above $10,000 require Controller sign-off; quarterly summary reported to CFO.",
            designScore: 3,
            implementationScore: 3,
            lastAssessed: new Date("2025-10-18"),
            status: "complete",
          },
        ],
      },
    ],
  },
  // Journal entry processing
  {
    processId: "proc-journal-entry",
    risks: [
      {
        id: "risk-je-01",
        riskNumber: "R-01",
        title: "Unauthorized journal entries",
        description:
          "Manual journal entries may be posted without appropriate review or approval, enabling fraudulent financial reporting.",
        owner: "Jenny Zhan",
        inherentScore: {
          components: {complexity: 2, subjectivity: 2, change: 2, uncertainty: 3, fraudSusceptibility: 4},
          rationale:
            "High inherent fraud susceptibility — manual journal entries are a primary fraud vector for financial statement manipulation. Moderate uncertainty around completeness of review coverage.",
          assessedBy: "Platformed AI",
          assessedAt: new Date("2025-12-01"),
        },
        residualLevel: "low",
        residualScore: {
          components: {complexity: 1, subjectivity: 1, change: 1, uncertainty: 1, fraudSusceptibility: 2},
          rationale:
            "Dual approval (JE-01) and system-enforced segregation of duties (JE-02) together provide highly effective preventive controls. The system-level enforcement leaves minimal room for override, reducing fraud susceptibility and uncertainty to low levels.",
          assessedBy: "Platformed AI",
          assessedAt: new Date("2025-12-01"),
        },
        status: "complete",
        cotabdAssertions: [
          {cotabdId: "cotabd-fr-transactions", assertionId: "existence"},
          {cotabdId: "cotabd-fr-transactions", assertionId: "accuracy"},
        ],
        controls: [
          {
            id: "ctrl-je-01a",
            controlNumber: "JE-01",
            title: "Dual Approval for Manual Entries",
            description:
              "All manual journal entries require preparer and independent reviewer approval; entries above materiality require Controller sign-off.",
            designScore: 3,
            implementationScore: 3,
            lastAssessed: new Date("2025-11-14"),
            status: "complete",
          },
          {
            id: "ctrl-je-01b",
            controlNumber: "JE-02",
            title: "Segregation of Duties",
            description:
              "System enforces that journal entry preparer cannot be the same individual who approves the entry.",
            designScore: 3,
            implementationScore: 3,
            lastAssessed: new Date("2025-11-14"),
            status: "complete",
          },
        ],
      },
      {
        id: "risk-je-02",
        riskNumber: "R-02",
        title: "Incorrect period posting",
        description:
          "Journal entries may be posted to incorrect accounting periods, causing cut-off errors in financial statements.",
        owner: "James Park",
        inherentScore: {
          components: {complexity: 3, subjectivity: 2, change: 4, uncertainty: 3, fraudSusceptibility: 2},
          rationale:
            "High inherent change risk from recent SAP period control configuration updates. Complexity in managing multiple period boundaries across entities and sub-ledgers.",
          assessedBy: "Platformed AI",
          assessedAt: new Date("2025-12-01"),
        },
        residualLevel: "medium",
        residualScore: {
          components: {complexity: 2, subjectivity: 2, change: 3, uncertainty: 2, fraudSusceptibility: 1},
          rationale:
            "SAP period controls (JE-03) restrict posting to closed periods, effectively reducing complexity and uncertainty. However, the Controller override exception process — rated only partially effective — means change risk from the recent configuration updates is not fully addressed.",
          assessedBy: "Platformed AI",
          assessedAt: new Date("2025-12-01"),
        },
        status: "in-review",
        cotabdAssertions: [
          {cotabdId: "cotabd-fr-transactions", assertionId: "cutoff"},
          {cotabdId: "cotabd-fr-transactions", assertionId: "accuracy"},
        ],
        controls: [
          {
            id: "ctrl-je-02a",
            controlNumber: "JE-03",
            title: "Period Close Controls",
            description:
              "SAP period controls restrict posting to closed periods; exceptions require Controller override with documented justification.",
            designScore: 2,
            implementationScore: 2,
            lastAssessed: new Date("2025-11-10"),
            status: "in-progress",
          },
        ],
      },
    ],
  },
  // Month-end close & reconciliation
  {
    processId: "proc-month-end-close",
    risks: [
      {
        id: "risk-mc-01",
        riskNumber: "R-01",
        title: "Incomplete reconciliations",
        description:
          "Balance sheet accounts may not be fully reconciled, allowing undetected errors or misstatements to persist across periods.",
        owner: "Lisa Wong",
        inherentScore: {
          components: {complexity: 4, subjectivity: 3, change: 3, uncertainty: 3, fraudSusceptibility: 2},
          rationale:
            "High inherent complexity from the volume and variety of balance sheet accounts requiring reconciliation. Subjectivity in determining reconciliation adequacy for complex accounts.",
          assessedBy: "Platformed AI",
          assessedAt: new Date("2025-11-28"),
        },
        residualLevel: "medium",
        residualScore: {
          components: {complexity: 3, subjectivity: 2, change: 2, uncertainty: 2, fraudSusceptibility: 1},
          rationale:
            "BlackLine reconciliation tracking (MEC-01) automates completeness monitoring and escalation, significantly reducing uncertainty and fraud susceptibility. Accounting manager review (MEC-02) adds quality assurance, though its partially effective implementation means some complexity persists for manual reconciliations.",
          assessedBy: "Platformed AI",
          assessedAt: new Date("2025-11-28"),
        },
        status: "in-review",
        cotabdAssertions: [
          {cotabdId: "cotabd-fr-balance", assertionId: "completeness"},
          {cotabdId: "cotabd-fr-balance", assertionId: "existence"},
          {cotabdId: "cotabd-fr-disclosure", assertionId: "completeness"},
        ],
        controls: [
          {
            id: "ctrl-mc-01a",
            controlNumber: "MEC-01",
            title: "BlackLine Reconciliation Tracking",
            description:
              "BlackLine tracks completion of all required account reconciliations with automated escalation for overdue items.",
            designScore: 3,
            implementationScore: 3,
            lastAssessed: new Date("2025-11-06"),
            status: "complete",
          },
          {
            id: "ctrl-mc-01b",
            controlNumber: "MEC-02",
            title: "Reconciliation Review",
            description:
              "Accounting manager reviews and certifies all high-risk account reconciliations within 5 business days of period close.",
            designScore: 3,
            implementationScore: 2,
            lastAssessed: new Date("2025-11-06"),
            status: "in-review",
          },
        ],
      },
      {
        id: "risk-mc-02",
        riskNumber: "R-02",
        title: "Unrecorded adjustments",
        description:
          "Necessary adjusting entries (accruals, deferrals, estimates) may be missed during the close process, misstating results.",
        owner: null,
        inherentScore: {
          components: {complexity: 4, subjectivity: 4, change: 5, uncertainty: 4, fraudSusceptibility: 2},
          rationale:
            "Very high inherent change risk from recent restructuring creating new accrual requirements across business units. High complexity and subjectivity in identifying all required adjustments, particularly for estimates.",
          assessedBy: "Platformed AI",
          assessedAt: new Date("2025-11-28"),
        },
        residualLevel: "significant",
        residualScore: {
          components: {complexity: 3, subjectivity: 3, change: 4, uncertainty: 3, fraudSusceptibility: 1},
          rationale:
            "The close calendar and checklist (MEC-03) provides a structured framework that reduces complexity and eliminates fraud susceptibility. However, the checklist cannot fully anticipate new adjustment requirements from the recent restructuring, leaving change risk high. Judgment-dependent estimates remain a residual subjectivity concern.",
          assessedBy: "Platformed AI",
          assessedAt: new Date("2025-11-28"),
        },
        status: "in-progress",
        cotabdAssertions: [
          {cotabdId: "cotabd-fr-balance", assertionId: "completeness"},
          {cotabdId: "cotabd-fr-disclosure", assertionId: "completeness"},
          {cotabdId: "cotabd-fr-disclosure", assertionId: "accuracy"},
        ],
        controls: [
          {
            id: "ctrl-mc-02a",
            controlNumber: "MEC-03",
            title: "Close Calendar & Checklist",
            description:
              "Structured close calendar with mandatory task checklist ensures all recurring adjustments are addressed by assigned owners.",
            designScore: 3,
            implementationScore: 3,
            lastAssessed: new Date("2025-11-06"),
            status: "complete",
          },
        ],
      },
    ],
  },
  // Intercompany eliminations
  {
    processId: "proc-intercompany",
    risks: [
      {
        id: "risk-ic-01",
        riskNumber: "R-01",
        title: "Unmatched intercompany transactions",
        description:
          "Intercompany transactions may not net to zero across entities due to timing differences or recording errors, distorting consolidated results.",
        owner: "James Park",
        inherentScore: {
          components: {complexity: 4, subjectivity: 2, change: 3, uncertainty: 3, fraudSusceptibility: 2},
          rationale:
            "High inherent complexity from multi-entity, multi-currency matching across jurisdictions with different close timelines. Timing differences create natural matching challenges.",
          assessedBy: "Platformed AI",
          assessedAt: new Date("2025-11-30"),
        },
        residualLevel: "medium",
        residualScore: {
          components: {complexity: 3, subjectivity: 1, change: 2, uncertainty: 2, fraudSusceptibility: 1},
          rationale:
            "Automated EPM matching with tolerance thresholds (IC-01) substantially reduces subjectivity and fraud susceptibility by standardising the matching process. Escalation to entity controllers addresses most timing differences, though multi-entity complexity inherently limits further reduction.",
          assessedBy: "Platformed AI",
          assessedAt: new Date("2025-11-30"),
        },
        status: "in-progress",
        cotabdAssertions: [{cotabdId: "cotabd-fr-balance", assertionId: "completeness"}],
        controls: [
          {
            id: "ctrl-ic-01a",
            controlNumber: "IC-01",
            title: "Monthly IC Matching",
            description:
              "Automated intercompany matching in EPM with tolerance thresholds; unmatched items escalated to entity controllers within 3 business days.",
            designScore: 3,
            implementationScore: 3,
            lastAssessed: new Date("2025-10-31"),
            status: "complete",
          },
        ],
      },
      {
        id: "risk-ic-02",
        riskNumber: "R-02",
        title: "Incorrect elimination entries",
        description:
          "Elimination entries may be incomplete or incorrectly calculated, leading to double-counting or omission of intercompany activity in consolidated statements.",
        owner: null,
        inherentScore: null,
        residualLevel: null,
        residualScore: null,
        status: "not-ready",
        cotabdAssertions: [{cotabdId: "cotabd-fr-balance", assertionId: "completeness"}],
        controls: [
          {
            id: "ctrl-ic-02a",
            controlNumber: "IC-02",
            title: "Elimination Entry Review",
            description:
              "Consolidation accountant prepares elimination entries with independent review by group accounting manager before posting.",
            designScore: 3,
            implementationScore: 3,
            lastAssessed: new Date("2025-10-31"),
            status: "complete",
          },
          {
            id: "ctrl-ic-02b",
            controlNumber: "IC-03",
            title: "IC Balance Confirmation",
            description:
              "Quarterly confirmation of intercompany balances between entity controllers with variance investigation above $25K.",
            designScore: null,
            implementationScore: null,
            lastAssessed: null,
            status: "not-ready",
          },
        ],
      },
    ],
  },
];
